// Receita lançada à mão LIGADA A CONTRATO — a régua da tela (05/10/2026).
//
// O resultado por contrato lia toda receita manual como "da empresa". O caso
// que pediu isto: o adiantamento da Corsan (HY25014) — três lançamentos tiram
// R$ 496,1 mil de dez/2025 e devolvem em jan e abr/2026; a DRE geral ficava
// certa e o contrato seguia com toda a receita em 2025 e só custo em 2026.
//
// Espelha dre-engine/handlers/manuais.js: só RECEITA BRUTA aceita contrato
// (as outras receitas — financeira, outras operacionais — não são de contrato).

export const GRUPO_QUE_ACEITA_CONTRATO = 'RECEITA_BRUTA';

/** Este grupo da DRE pode ser ligado a um contrato? */
export const aceitaContrato = (grupo?: string | null): boolean => String(grupo || '') === GRUPO_QUE_ACEITA_CONTRATO;

export type ContratoSlim = {
  id: string;
  numero?: string | null;
  titulo?: string | null;
  status?: string | null;
  contrato_pai_id?: string | null;
  cliente_nome?: string | null;
};

/**
 * Opções do seletor de contrato. Só contrato PRINCIPAL (aditivo sobe para o pai
 * no resultado, então escolher o aditivo não mudaria nada) e nunca cancelado.
 * Encerrado/concluído ENTRA: o adiantamento que se acerta é quase sempre de
 * contrato antigo. Mais novo primeiro. `atual` garante que o contrato já
 * gravado apareça mesmo que a regra o tirasse (não se perde o que está salvo) —
 * e, se ele nem veio na lista (a lista do servidor não traz aditivo e corta em
 * 500), entra pelo `rotuloAtual`: o campo nunca mostra um identificador.
 */
export function opcoesDeContrato(lista: ContratoSlim[], atual?: string | null, rotuloAtual?: string | null): { value: string; label: string }[] {
  const rotulo = (c: ContratoSlim) => [c.numero || '(sem número)', c.titulo || c.cliente_nome || ''].filter(Boolean).join(' · ');
  const opcoes = (lista || [])
    .filter(c => c && c.id && (c.id === atual || (!c.contrato_pai_id && String(c.status || '') !== 'cancelado')))
    .slice()
    .sort((a, b) => String(b.numero || '').localeCompare(String(a.numero || ''), 'pt-BR', { numeric: true }))
    .map(c => ({ value: c.id, label: rotulo(c) }));
  if (atual && !opcoes.some(o => o.value === atual)) opcoes.push({ value: atual, label: rotuloAtual || 'Contrato gravado (fora da lista)' });
  return opcoes;
}

/** Ao salvar, esta receita vai PERDER o contrato? Acontece quando alguém troca a
 *  categoria de uma receita bruta com contrato para outra coisa: o campo some
 *  da tela e o servidor desliga — então a tela avisa antes. */
export const perdeOContrato = (grupo?: string | null, contratoGravado?: string | null): boolean =>
  !!contratoGravado && !aceitaContrato(grupo);

/**
 * O que mandar em `contrato_id`.
 *   criar:  só manda quando o grupo aceita e há escolha (senão não fala do assunto)
 *   editar: manda SEMPRE que o grupo aceita (null limpa); grupo que não aceita
 *           não manda — o servidor já tira o contrato de receita que virou despesa.
 */
export function contratoParaEnviar(o: { grupo?: string | null; escolha?: string | null; editando: boolean }): string | null | undefined {
  if (!aceitaContrato(o.grupo)) return undefined;
  if (o.escolha) return o.escolha;
  return o.editando ? null : undefined;
}

export type RespostaDoSalvar = {
  aviso?: string | null;
  resultado_contrato?: { refeito?: string[]; pendente?: string[] } | null;
};

/**
 * O salvar de um lançamento NOVO falhou — mas chegou a gravar? Sem resposta do
 * servidor (rede caiu, tempo esgotado) ou com erro 5xx (o servidor quebrou no
 * meio do caminho), não dá para saber: o lançamento pode ter sido gravado e só
 * a resposta se perdeu. Recusa do servidor (4xx) é erro comum: nada foi gravado. Dizer "Erro ao salvar" com o formulário ainda preenchido
 * convida a clicar de novo — e nasce uma receita em dobro na DRE (o estorno da
 * Corsan são R$ 496 mil). Nesse caso a tela avisa e recarrega a lista.
 * `enviado` = o pedido chegou a sair (falha antes disso é erro comum).
 */
export function erroAoSalvarNovo(e: any, enviado: boolean): { incerto: boolean; texto: string } {
  const status = e && e.response ? e.response.status : null;
  if (enviado && (!e || !e.response || (typeof status === 'number' && status >= 500))) {
    return { incerto: true, texto: 'Não deu para confirmar se o lançamento foi gravado: o servidor não respondeu como devia. '
      + 'Atualizei a lista — confira se ele já está nela ANTES de lançar de novo.' };
  }
  const doServidor = e && e.response && e.response.data ? (e.response.data.erro || e.response.data.error) : null;
  return { incerto: false, texto: 'Erro ao salvar lançamento' + (doServidor ? `: ${doServidor}` : '.') };
}

/** A mensagem depois de salvar: sucesso simples, sucesso dizendo que o
 *  resultado do contrato foi refeito, ou AVISO quando ele ficou por refazer. */
export function mensagemDepoisDeSalvar(resp: RespostaDoSalvar | null | undefined, base: string): { tipo: 'success' | 'warning'; texto: string } {
  const r = resp || {};
  if (r.aviso) return { tipo: 'warning', texto: r.aviso };
  const refeito = (r.resultado_contrato && r.resultado_contrato.refeito) || [];
  if (refeito.length) return { tipo: 'success', texto: `${base} Resultado por contrato de ${refeito.join(' e ')} refeito.` };
  return { tipo: 'success', texto: base };
}
